import { isValidElement, type ReactNode } from 'react';

const REDDIT_POST_URL_PATTERNS = [
	/^https:\/\/(www\.|old\.|new\.)?reddit\.com\/r\/[^/]+\/comments\/[^/?#]+(\/[^?#]*)?$/,
	/^https:\/\/redd\.it\/[A-Za-z0-9]+$/,
];

// MUST stay in sync with isRedditPostUrl in services/ts/web-proxy/src/proxies/redditProxy.ts
export function isRedditPostUrl(url: string): boolean {
	return REDDIT_POST_URL_PATTERNS.some((re) => re.test(url));
}

const SUBREDDIT_PATTERN =
	/^https:\/\/(?:www\.|old\.|new\.)?reddit\.com\/r\/([^/]+)\//;

/**
 * The subreddit a post URL names. A redd.it short link carries no subreddit, so
 * it returns null and the card leaves that line out.
 */
export function subredditFromUrl(url: string): string | null {
	return SUBREDDIT_PATTERN.exec(url)?.[1] ?? null;
}

export function flattenToString(node: ReactNode): string {
	if (node == null || typeof node === 'boolean') return '';
	if (typeof node === 'string') return node;
	if (typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(flattenToString).join('');
	if (isValidElement(node)) {
		const children = (node.props as { children?: ReactNode }).children;
		return flattenToString(children);
	}
	return '';
}

export function getStandaloneAnchor(
	children: ReactNode,
): { href: string; text: string } | null {
	const list = Array.isArray(children) ? children : [children];
	const nonWhitespace = list.filter((child) => {
		if (typeof child === 'string') return child.trim().length > 0;
		return child != null && typeof child !== 'boolean';
	});
	if (nonWhitespace.length !== 1) return null;

	const only = nonWhitespace[0];
	if (!isValidElement(only)) return null;

	const props = only.props as { href?: unknown; children?: ReactNode };
	if (typeof props.href !== 'string') return null;

	const text = flattenToString(props.children).trim();
	return { href: props.href, text: text.length > 0 ? text : props.href };
}
